import { Flex, Spacer, Skeleton, Box, Card, CardBody } from '@chakra-ui/react';
import React, { useState, useMemo } from 'react';

import { ConcentrationLimitInsightsGqlDataGQLType } from 'types/dashboard-core-gqlTypes.generated';

import { PortfolioChartCards } from '../shared/PortfolioChartCards';
import {
  ChartTimePeriod,
  formatCsvDownloadDataRows,
  getInitialActiveFields,
  onToggleActiveField,
  PortfolioChartConfig,
} from '../shared/portfolioChartUtils';
import { PortfolioDownloadCsvLink } from '../shared/PortfolioDownloadCsvLink';
import { PortfolioReChart } from '../shared/PortfolioReChart';
import { Typography } from '~/packages/common-frontend/components/Typography';

type PortfolioConcentrationLimitsChartTemplateProps = {
  creditFacilityId: string;
  concentrationLimitKey: string;
  chartTitle: string;
  chartConfig: PortfolioChartConfig<any>;
  chartData: ConcentrationLimitInsightsGqlDataGQLType[];
  selectedTimePeriod: ChartTimePeriod;
};
export const PortfolioConcentrationLimitsChartTemplate = ({
  creditFacilityId: _creditFacilityId,
  concentrationLimitKey: _concentrationLimitKey,
  chartTitle,
  chartConfig,
  chartData,
  selectedTimePeriod,
}: PortfolioConcentrationLimitsChartTemplateProps) => {
  const initialActiveFields = getInitialActiveFields({
    chartConfig,
  }) as (keyof ConcentrationLimitInsightsGqlDataGQLType)[];

  const [
    selectedBar,
    setSelectedBar,
  ] = useState<ConcentrationLimitInsightsGqlDataGQLType | null>(null);

  const [activeFields, setActiveFields] = useState<
    (keyof ConcentrationLimitInsightsGqlDataGQLType)[]
  >(initialActiveFields);

  // Update selected bar when chart data changes
  React.useEffect(() => {
    if (chartData.length > 0) {
      setSelectedBar(chartData[chartData.length - 1]);
    }
  }, [chartData]);

  const { dataRows, headers } = useMemo(
    () =>
      formatCsvDownloadDataRows<ConcentrationLimitInsightsGqlDataGQLType>(
        chartData,
        activeFields,
      ),
    [chartData, activeFields],
  );

  if (!chartData || chartData.length === 0) {
    return <Skeleton height="700px" />;
  }

  return (
    <Card variant="outline" size="lg">
      <CardBody display="flex">
        <Box width="70%">
          <Typography variant="section-subtitle">{chartTitle}</Typography>

          {/* child 1 */}
          <PortfolioReChart
            chartData={chartData}
            selectedTimePeriod={selectedTimePeriod}
            currentSelectedBarReportingDate={
              selectedBar
                ? selectedBar.reportingDate
                : chartData[chartData.length - 1]?.reportingDate
            }
            onSelectBar={setSelectedBar}
            chartConfig={chartConfig}
            visibleChartComponents={activeFields}
          />
        </Box>
        <Spacer />
        {/* child 2 */}
        {selectedBar && (
          <Flex width="25%" flexDirection="column">
            <PortfolioChartCards
              selectedBarData={selectedBar}
              chartConfig={chartConfig}
              activeFields={activeFields}
              onToggleActiveField={(selectedField) =>
                onToggleActiveField({
                  selectedField,
                  currentActiveFields: activeFields,
                  setActiveFields,
                })
              }
            />
            <Spacer />
            <PortfolioDownloadCsvLink
              dataRows={dataRows}
              headers={headers}
              chartTitle={chartTitle}
              selectedTimePeriod={selectedTimePeriod}
            />
          </Flex>
        )}
      </CardBody>
    </Card>
  );
};
